import { Injectable } from "@angular/core";
import { CulturalActivity } from "@common/ADAPT.Common.Model/organisation/cultural-activity";
import { SpeedCatchup } from "@common/ADAPT.Common.Model/organisation/speed-catchup";
import { CatchupRatingType } from "@common/ADAPT.Common.Model/organisation/speed-catchup-rating";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { CulturalActivityService } from "@org-common/lib/cultural-activity/cultural-activity.service";
import { switchMap, tap } from "rxjs/operators";
import { CatchupHistoryChartDialogComponent } from "./catchup-history-chart-dialog/catchup-history-chart-dialog.component";
import { myCatchupsPageRoute, personalCatchupsPageRoute } from "./my-catchups-page/my-catchups-page.route";
import { PeerCatchupService } from "./peer-catchup.service";
import { RecordCatchupDialogComponent } from "./record-catchup-dialog/record-catchup-dialog.component";
import { ShowCatchupDialogComponent } from "./show-catchup-dialog/show-catchup-dialog.component";

@Injectable({
    providedIn: "root",
})
export class PeerCatchupUiService {
    public constructor(
        private dialogService: AdaptCommonDialogService,
        private peerCatchupService: PeerCatchupService,
        private culturalActivityService: CulturalActivityService,
    ) {}

    public showCatchup(
        catchup: SpeedCatchup,
        hideHistoryButton?: boolean,
        enterEditMode?: boolean,
        scrollToFacilitator?: boolean,
        scrollToRatingTypeName?: CatchupRatingType,
    ) {
        return this.dialogService.open(ShowCatchupDialogComponent, {
            catchup,
            hideHistoryButton,
            enterEditMode,
            showFacilitatorSection: scrollToFacilitator,
            focusRatingType: scrollToRatingTypeName,
        });
    }

    public deleteCatchup(catchup: SpeedCatchup) {
        let itemLinkMessage = "";

        if (catchup.links.length) {
            itemLinkMessage = "<p>Note that all actions will be unlinked from the Peer Catch-up.</p>";
        }

        return this.dialogService.openConfirmationDialog({
            title: "Delete Peer Catch-up",
            message: `<p>Are you sure you wish to delete this Peer Catch-up between ${catchup.person1!.fullName} & ${catchup.person2!.fullName}?</p>` + itemLinkMessage,
            confirmButtonText: "Yes",
            cancelButtonText: "No",
        }).pipe(
            switchMap(() => this.peerCatchupService.remove(catchup)),
            switchMap(() => this.peerCatchupService.saveEntities(catchup)),
            tap(() => this.peerCatchupService.emitCatchupChange(catchup)), // detached entity will be emitted
        );
    }

    public showCatchupHistoryChart(person1: Person, person2: Person) {
        return this.dialogService.open(CatchupHistoryChartDialogComponent, { person1, person2 });
    }

    public recordNewCatchup(person1Id: number, person2Id?: number, teamId?: number) {
        // create catchup outside of record dialog to make restoring unsaved entity easier -> entity restore before passed into the dialog
        // component
        return this.peerCatchupService.createNewCatchup(person1Id, person2Id, teamId).pipe(
            switchMap((catchup) => this.dialogService.open(RecordCatchupDialogComponent, { catchup })),
            tap((savedCatchup) => this.peerCatchupService.emitCatchupChange(savedCatchup)),
        );
    }

    public recordNewCatchupFromActivity(culturalActivity: CulturalActivity) {
        return this.peerCatchupService.createNewCatchup(culturalActivity.person1Id!, culturalActivity.person2Id, culturalActivity.teamId).pipe(
            switchMap((catchup) => this.dialogService.open(RecordCatchupDialogComponent, { catchup })),
            tap((savedCatchup) => this.peerCatchupService.emitCatchupChange(savedCatchup)),
        );
    }

    public scheduleCatchupFromPreviousCatchup(catchup: SpeedCatchup, teamId?: number) {
        return this.scheduleCatchup(catchup.person1Id, catchup.person2Id, teamId ?? catchup.teamId, catchup.creationDate);
    }

    public scheduleCatchup(person1Id: number, person2Id?: number, teamId?: number, fromDate?: Date) {
        return this.peerCatchupService.scheduleCatchup(person1Id, person2Id, teamId, fromDate).pipe(
            switchMap((activity) => this.culturalActivityService.rescheduleActivity(activity, false)),
        );
    }

    public rescheduleCatchupActivity(culturalActivity: CulturalActivity) {
        return this.culturalActivityService.rescheduleActivity(culturalActivity, true);
    }

    public getDashboardUrl(person?: Person) {
        return person
            ? personalCatchupsPageRoute.getRoute({ personId: person.personId })
            : myCatchupsPageRoute.getRoute();
    }
}
